import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Column } from 'react-table'
import { UseMutationOptions, UseQueryOptions, useMutation, useQuery } from '@tanstack/react-query'
import axios, { AxiosResponse } from 'axios'
import CheckboxField from 'components/CheckboxField'
import { ReactComponent as ViewIcon } from 'assets/images/Eye.svg'
import { useUrlQuery } from 'hooks/url'
import { getClientDetails, useMyProfile, useSortHandler } from 'hooks/common'
import { ENDPOINTS } from 'const/endpoints'
import { IAxiosResponseWithPagination } from 'types/entities'
import { formatDate } from 'utils/time'
import { ISessionClaimParams } from './ClaimsGeneralInvoice.interface'
import { createClaimsInvoice, getSessionClaimsListing } from './ClaimsGeneralInvoice.service'
import { ROLES } from 'const/auth'
import { onGetTableColumns, onGetTablebodyColumn } from 'utils/common'
import { IProgramDashboardParams } from 'modules/ProgramManagerDashboard/Dashboard.interface'
import { getProgramListing } from 'modules/ProgramManagerDashboard/Dashboard.service'

export function useClaimsGeneralInvoiceTableData({
	onOpen,
	tableSectionDetail,
}: {
	onOpen: any
	tableSectionDetail?: any
}): [
	Column<{ [key: string]: React.ReactNode }>[],
	{ [key: string]: React.ReactNode }[],
	(param: string) => void,
	string[],
	React.Dispatch<React.SetStateAction<string[]>>,
	any,
] {
	/**
	 * *Variable and hooks section
	 */
	const [params, setParams] = useUrlQuery<ISessionClaimParams>()
	const param = useParams<{ id: string }>()
	const { data: claimsNow } = useSessionClaimsNowListing({
		...params,
		clientId: getClientDetails()?.clientId,
	})
	const onSort = useSortHandler(params.sort)
	const { id } = param
	const profile = useMyProfile()
	const isCHCT = profile.role === ROLES.CHCT
	/**
	 * *State section
	 */
	const [selectedReviewId, setSelectedReviewId] = useState<string[] | []>([])
	/**
	 * *Function section
	 */
	const handleCheckboxChange = (e: any, id: string | undefined = '', item: any) => {
		const checked = e.target.checked
		if (checked) {
			setSelectedReviewId((old) => [...old, id])
		} else {
			const data = selectedReviewId?.filter((item) => {
				return item !== id
			})
			setSelectedReviewId(data)
		}
	}

	/**
	 * *To filter action particular column
	 */
	const dynamicColumn = tableSectionDetail?.providerColumns
	const claimsGeneralInvoiceTableColumn = onGetTableColumns(dynamicColumn)

	/**
	 * *useMemo section
	 */
	const claimNowResponse = useMemo(() => {
		const mapBodyObj = (item: any) => {
			return {
				checkBox: (
					<div className="flex gap-2 pt-2">
						<CheckboxField
							className="!mb-0 md:col-span-2 test2"
							checked={
								selectedReviewId?.find((review) => {
									return review === item.id
								})
									? true
									: false
							}
							disabled={item?.claimStatus === 'Review'}
							onChange={(e) => handleCheckboxChange(e, item?.id, item)}
							classNames={{ content: '', children: 'text-sm' }}
						></CheckboxField>
					</div>
				),
				dateOfSubmit: <div>{item?.dos ? formatDate(+item?.dos) : '--'}</div>,
				patientProgramId: (
					<div className=""> {item?.patient?.program?.patientProgramId ?? '--'}</div>
				),
				programTitle: (
					<div className="">{item?.program?.programCriteria?.programTitle ?? '--'}</div>
				),
				title: <div className="">{item?.session?.title ?? '--'}</div>,

				cpt: (
					<div className="flex items-center gap-2">
						{/* <Link
							to={
								item?.claimStatus == 'Review'
									? APP_ROUTES.CLAIM_NOW + '/' + param?.id + `?partner=${id}&&cptlist=${item?.id}`
									: APP_ROUTES.CLAIM_NOW + '/' + param?.id + `?partner=${id}`
							}
							className="text-blue-500"
						> */}
						<span
							data-tip="Open"
							onClick={() => {
								if (item?.claimStatus == 'Review') {
									setParams((prev) => {
										return {
											...prev,
											partner: id ?? undefined,
											cptlist: item?.id ?? undefined,
										}
									})
									onOpen()
								}
							}}
						>
							<ViewIcon
								className={`
										  ${
												item?.claimStatus == 'Review'
													? 'hover:stroke-fountainBlue  stroke-solidGray cursor-pointer'
													: 'bg-slate-200 cursor-not-allowed stroke-solidGray'
											}`}
							/>
						</span>
						{/* </Link> */}
					</div>
				),
				approved: <div className=""> ${item?.approved}</div>,
				outstanding: <div className=""> ${item?.outstanding}</div>,
				performedBy: <div className=""> {item?.performer?.humanName?.text || '--'}</div>,
				declined: <div className=""> ${item?.declined}</div>,
				status: (
					<div className="flex gap-2 items-center">
						<div className="border-[#00BAC7] border bg-[#E2F9FB] w-max px-3 py-1 rounded-md">
							{item?.claimStatus}
						</div>
					</div>
				),
			}
		}
		return (
			claimsNow?.data?.items?.map((item: any) => {
				const bodyobj = mapBodyObj(item)
				return onGetTablebodyColumn(item, bodyobj, claimsGeneralInvoiceTableColumn)
			}, []) || []
		)
	}, [
		claimsNow?.data.items,
		selectedReviewId,
		setSelectedReviewId,
		claimsGeneralInvoiceTableColumn,
	])

	return [
		claimsGeneralInvoiceTableColumn,
		claimNowResponse,
		onSort,
		selectedReviewId,
		setSelectedReviewId,
		claimsNow,
	]
}

/**
 * * Using the React Hooks useQuery to create a Query function to show Data in Table..
 */
interface ISessionClaimsNowQueryProps
	extends UseQueryOptions<IAxiosResponseWithPagination<Partial<any>>>,
		Partial<ISessionClaimParams> {}

export function useSessionClaimsNowListing({
	clientId,
	page,
	sort,
	limit,
	search,
	dateOfSubmitted,
	patientId,
	programTitle,
	sessionTitle,
	outstanding,
	approved,
	declined,
	claimStatus,
	provider,
	partner,
	program,
	...rest
}: ISessionClaimsNowQueryProps = {}) {
	return useQuery<
		IAxiosResponseWithPagination<Partial<any>>,
		any,
		IAxiosResponseWithPagination<Partial<any>>
	>(
		[
			ENDPOINTS.SESSION_FEESCHEDULE_CLAIMS,
			page,
			limit,
			sort,
			search,
			dateOfSubmitted,
			patientId,
			programTitle,
			sessionTitle,
			outstanding,
			approved,
			declined,
			claimStatus,
			partner,
			clientId,
			provider,
			program,
		],
		() => {
			const CancelToken = axios.CancelToken
			const source = CancelToken.source()
			return getSessionClaimsListing(
				clientId,
				{
					page,
					limit,
					sort,
					search,
					dateOfSubmitted,
					patientId,
					programTitle,
					sessionTitle,
					outstanding,
					approved,
					declined,
					claimStatus,
					partner,
					provider,
					program,
				},
				source,
			)
		},
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			suspense: false,
			...rest,
		},
	)
}
/**
 * * Using the React Hooks useMutation to create a Mutation function to generate a PDF..
 */
type IClaimsInvoiceProps = Partial<UseMutationOptions<AxiosResponse<any>, any, Partial<any>>>
export function useCreateClaimsInvoice({ ...rest }: IClaimsInvoiceProps = {}) {
	return useMutation<AxiosResponse<any>, any, Partial<any>>(createClaimsInvoice, {
		...rest,
	})
}

interface IProgramQueryProps
	extends UseQueryOptions<IAxiosResponseWithPagination<Partial<any>>>,
		Partial<IProgramDashboardParams> {
	isEnabled?: boolean
}
export function useGetProgramFilter({
	limit,
	page,
	clientId,
	search,
	isEnabled = true,
	...rest
}: IProgramQueryProps) {
	return useQuery<any>(
		[ENDPOINTS.CLIENT_PROGRAM, limit, page, clientId, search],
		() => {
			const CancelToken = axios.CancelToken
			const source = CancelToken.source()

			return getProgramListing(source, clientId, { limit, page, search })
		},
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			enabled: isEnabled,
			...rest,
		},
	)
}
